import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { useAthleteLessons } from '../api/use-athlete-lessons.js';
import { readAthleteSignature, type SignatureView } from '../api/signature-view.js';
import { listMyTeams, type MyTeamPillRow } from '../api/my-teams.js';
import { LessonRow } from '../components/athlete-lesson-row.js';
import { SignatureCodeLine } from '../components/signature-codes.js';
import { TeamPill } from '../components/team-pill.js';
import { PracticesHomeRow } from '../components/practices-home-row.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * The athlete's Home, where they land (Athlete Portal canvas, "Noa · Home",
 * approved 16 October): their next lesson, then their latest one with the first
 * line of its plan - the parent's Home, for one athlete and nothing else.
 *
 * My Performance Signature sits between them (stage 4a, mockup *Signature in
 * the Portal*), once the coach has entered a code; nothing shows before.
 * Under it, a My Team pill for each team they play on (Teams stage 3, board
 * 5, approved 25 October).
 *
 * NEXT CLINIC (Matthew, 31 October) is its own section, as on the parent's Home:
 * whichever of it and Next lesson comes first in time is on top, and Next lesson
 * says nothing when a clinic is all they have.
 */
export function AthleteHomeRoute() {
  const state = useAthleteLessons();
  const signature = useOwnSignature();
  const teams = useOwnTeams();

  if (state.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (state.status === 'failed') return <p className="empty">{state.message}</p>;

  const now = Date.now();
  const coming = state.lessons
    .filter((lesson) => new Date(lesson.end).getTime() >= now)
    .sort((a, b) => a.start.localeCompare(b.start));
  const next = coming.find((lesson) => !lesson.is_clinic);
  const nextClinic = coming.find((lesson) => lesson.is_clinic);
  const clinicFirst = nextClinic !== undefined && (!next || Date.parse(nextClinic.start) < Date.parse(next.start));
  const clinicPanel = nextClinic ? (
    <section className="panel">
      <h2>Next clinic</h2>
      <LessonRow lesson={nextClinic} withPlace />
    </section>
  ) : null;
  const latest = state.lessons
    .filter((lesson) => new Date(lesson.end).getTime() < now)
    .sort((a, b) => b.start.localeCompare(a.start))[0];

  return (
    <>
      {clinicFirst ? clinicPanel : null}

      {nextClinic && !next ? null : (
        <section className="panel">
          <h2>Next lesson</h2>
          {next ? <LessonRow lesson={next} withPlace /> : <p className="empty">Nothing booked yet.</p>}
        </section>
      )}

      {clinicFirst ? null : clinicPanel}

      {/* Practices (board R1): always shown, even at 0. */}
      <PracticesHomeRow athleteClientId={null} to="/practices" />

      {signature ? (
        <>
          <span className="section-label">My Performance Signature</span>
          <Link className="hour-row" to="/signature">
            <span className="hour-row-text">
              <span className="name">My Performance Signature</span>
              <SignatureCodeLine codes={signature.codes} />
            </span>
            <span className="chevron" aria-hidden="true">
              ›
            </span>
          </Link>
        </>
      ) : null}

      {teams.map((team) => (
        <TeamPill key={team.team_id} team={team} />
      ))}

      {latest ? (
        <>
          <span className="section-label">Last lesson</span>
          <LessonRow lesson={latest} showOutcome />
        </>
      ) : null}
    </>
  );
}

/** The athlete's signature, or null - Home never waits for it or says it failed. */
function useOwnSignature(): SignatureView | null {
  const getToken = useAccessToken();
  const [view, setView] = useState<SignatureView | null>(null);
  useEffect(() => {
    let live = true;
    readAthleteSignature(getToken)
      .then((found) => {
        if (live) setView(found.signature);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [getToken]);
  return view;
}

/** The teams the athlete plays on, or none - Home never waits for them either. */
function useOwnTeams(): MyTeamPillRow[] {
  const getToken = useAccessToken();
  const [teams, setTeams] = useState<MyTeamPillRow[]>([]);
  useEffect(() => {
    let live = true;
    listMyTeams(getToken)
      .then((found) => {
        if (live) setTeams(found.teams);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [getToken]);
  return teams;
}
